import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRegistry } from "@/context/RegistryContext";
import { PRIORITIES } from "@/data/marketplace";
import { ONBOARDING } from "@/constants/testIds";

const stepAnim = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { setProfile } = useRegistry();

  const [step, setStep] = useState(1);
  const [dueDate, setDueDate] = useState(null);
  const [priorities, setPriorities] = useState([]);

  const togglePriority = (id) => {
    setPriorities((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const canProceed =
    (step === 1 && !!dueDate) || (step === 2 && priorities.length > 0);

  const finish = () => {
    setProfile({ dueDate, priorities });
    navigate("/dashboard");
  };

  return (
    <div
      data-testid={ONBOARDING.root}
      className="min-h-[calc(100vh-5rem)] bg-[#FDFBF7] flex flex-col"
    >
      <div className="mx-auto w-full max-w-lg px-6 pt-10 md:pt-16">
        <div className="flex items-center gap-2">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`h-[3px] flex-1 rounded-full transition-colors duration-500 ${
                s <= step ? "bg-[#D98A71]" : "bg-[#E5E1D8]"
              }`}
            />
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between text-xs tracking-[0.2em] uppercase text-[#5C6B62]">
          <span>Step {step} of 2</span>
          <span>A gentle check-in</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 md:py-16">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="s1" {...stepAnim} data-testid={ONBOARDING.step1}>
                <p className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">
                  First things first
                </p>
                <h2 className="font-display text-4xl md:text-5xl text-[#2D3732] mt-4 leading-tight">
                  When are you
                  <br />
                  <span className="italic text-[#D98A71]">expecting?</span>
                </h2>
                <p className="mt-6 text-[#5C6B62] leading-relaxed">
                  We use your due date to time gentle reminders — never to pressure.
                  Feel free to change it later.
                </p>

                <div className="mt-10">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        data-testid={ONBOARDING.dueDateTrigger}
                        className="w-full h-14 rounded-2xl border border-[#E5E1D8] bg-white/60 hover:bg-white transition-colors px-5 flex items-center justify-between text-left"
                      >
                        <span className={dueDate ? "text-[#2D3732]" : "text-[#5C6B62]"}>
                          {dueDate
                            ? format(dueDate, "EEEE, d MMMM yyyy")
                            : "Choose your due date"}
                        </span>
                        <CalendarIcon size={18} strokeWidth={1.5} className="text-[#5C6B62]" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-[#FDFBF7] border-[#E5E1D8] rounded-2xl"
                      align="start"
                    >
                      <Calendar mode="single" selected={dueDate} onSelect={setDueDate} initialFocus />
                    </PopoverContent>
                  </Popover>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" {...stepAnim} data-testid={ONBOARDING.step3}>
                <p className="text-xs tracking-[0.25em] uppercase text-[#5C6B62]">
                  What she needs most
                </p>
                <h2 className="font-display text-4xl md:text-5xl text-[#2D3732] mt-4 leading-tight">
                  Where should
                  <br />
                  <span className="italic text-[#D98A71]">care land?</span>
                </h2>
                <p className="mt-6 text-[#5C6B62] leading-relaxed">
                  Pick everything that matters. We'll shape the marketplace around it.
                </p>

                <div className="mt-10 space-y-3">
                  {PRIORITIES.map((p) => {
                    const checked = priorities.includes(p.id);
                    return (
                      <label
                        key={p.id}
                        htmlFor={`p-${p.id}`}
                        className={`flex items-start gap-4 p-5 rounded-2xl border cursor-pointer transition-colors ${
                          checked
                            ? "border-[#D98A71] bg-[#D98A71]/8"
                            : "border-[#E5E1D8] bg-white/60 hover:bg-white"
                        }`}
                      >
                        <Checkbox
                          id={`p-${p.id}`}
                          data-testid={ONBOARDING.priorityCheckbox(p.id)}
                          checked={checked}
                          onCheckedChange={() => togglePriority(p.id)}
                          className="mt-1 border-[#5C6B62] data-[state=checked]:bg-[#D98A71] data-[state=checked]:border-[#D98A71]"
                        />
                        <div>
                          <div className="font-medium text-[#2D3732]">{p.label}</div>
                          <div className="text-sm text-[#5C6B62] mt-1">{p.hint}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-12 flex items-center justify-between">
            <button
              data-testid={ONBOARDING.back}
              onClick={() => (step > 1 ? setStep(step - 1) : navigate("/"))}
              className="inline-flex items-center gap-2 text-sm text-[#5C6B62] hover:text-[#2D3732] transition-colors"
            >
              <ArrowLeft size={16} strokeWidth={1.5} />
              Back
            </button>

            {step < 2 ? (
              <button
                data-testid={ONBOARDING.next}
                disabled={!canProceed}
                onClick={() => setStep(step + 1)}
                className="rounded-full bg-[#2D3732] text-[#FDFBF7] px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#1e2521] transition-colors"
              >
                Continue
                <ArrowRight size={16} strokeWidth={1.5} />
              </button>
            ) : (
              <button
                data-testid={ONBOARDING.finish}
                disabled={!canProceed}
                onClick={finish}
                className="rounded-full bg-[#D98A71] text-[#FDFBF7] px-7 py-3.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#c67a63] transition-colors"
              >
                See her marketplace
                <Check size={16} strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
